import type { IconType } from "react-icons";
import { FiAlertOctagon, FiCheckCircle, FiClock, FiHelpCircle, FiXOctagon } from "react-icons/fi";

import { useI18n, type Key } from "@/lib/i18n";
import type { RxLevel } from "@/lib/olthealth";
import type { CauseKind, OntLive } from "@/lib/ontstate";
import { cn } from "@/lib/utils";

/** ONT state, same words and colours on every page: icon + label + colour. */
export const LIVE_UI: Record<OntLive, { icon: IconType; text: string; bg: string; label: Key }> = {
  online: { icon: FiCheckCircle, text: "text-signal-good", bg: "bg-signal-good/10", label: "ont.live.online" },
  offline: { icon: FiXOctagon, text: "text-white", bg: "bg-signal-crit", label: "ont.live.offline" },
  missing: { icon: FiAlertOctagon, text: "text-signal-crit", bg: "bg-signal-crit/15", label: "ont.live.missing" },
  stale: { icon: FiClock, text: "text-amber-200", bg: "bg-amber-400/10", label: "ont.live.stale" },
  unknown: { icon: FiHelpCircle, text: "text-slate-300", bg: "bg-slate-700/40", label: "ont.live.unknown" },
};

export function LiveBadge({ live, big }: { live: OntLive; big?: boolean }) {
  const { t } = useI18n();
  const s = LIVE_UI[live];
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm font-semibold uppercase tracking-[0.12em]", s.text, s.bg,
      big ? "px-2.5 py-1 text-[12.5px]" : "px-1.5 py-0.5 text-[10.5px]")}>
      <s.icon className={big ? "size-4" : "size-3"} /> {t(s.label)}
    </span>
  );
}

export const RX_UI: Record<RxLevel, { text: string; dot: string; label: Key }> = {
  good: { text: "text-signal-good", dot: "bg-signal-good", label: "ont.rx.good" },
  moderate: { text: "text-slate-300", dot: "bg-slate-400", label: "ont.rx.moderate" },
  weak: { text: "text-signal-warn", dot: "bg-signal-warn", label: "ont.rx.weak" },
  critical: { text: "text-signal-crit", dot: "bg-signal-crit", label: "ont.rx.critical" },
  nodata: { text: "text-slate-500", dot: "bg-slate-600", label: "ont.rx.nodata" },
};

/** "● WEAK": the level word next to a reading, so colour is never the only signal. */
export function RxTag({ level }: { level: RxLevel }) {
  const { t } = useI18n();
  const s = RX_UI[level];
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-[10.5px] font-semibold uppercase tracking-[0.12em]", s.text)}>
      <span className={cn("size-1.5 rounded-full", s.dot)} /> {t(s.label)}
    </span>
  );
}

/** Measured wording for an OLT down reason; the raw code stays visible beside it. */
export function causeLabel(kind: CauseKind | null, t: (k: Key) => string): string {
  if (kind === "dyingGasp") return t("ont.cause.dyingGasp");
  if (kind === "los") return t("ont.cause.los");
  if (kind === "other") return t("ont.cause.other");
  return t("ont.cause.none");
}

/** "12m", "16h 33m", "2d 4h": durations the way the NOC says them. */
export function durText(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "—";
  const m = Math.floor(ms / 60_000);
  if (m < 1) return `${Math.max(0, Math.floor(ms / 1000))}s`;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export const agoText = (iso: string | null | undefined, nowMs: number) => (iso ? durText(nowMs - Date.parse(iso)) : "—");

export const distText = (m: number | null | undefined) =>
  m === null || m === undefined ? "—" : m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m} m`;
